/**
 * Environment Configuration
 * 
 * SINGLE SOURCE OF TRUTH for environment-specific settings.
 * 
 * This file centralizes all environment detection and feature flags.
 * Use these exports throughout the application instead of checking
 * import.meta.env directly.
 */

// Environment Detection
export const ENV = {
  /**
   * Current environment mode
   * Values: 'development' | 'staging' | 'production'
   */
  MODE: import.meta.env.MODE as 'development' | 'staging' | 'production',
  
  /**
   * Is this a development build?
   */
  IS_DEV: import.meta.env.DEV,
  
  /**
   * Is this a production build?
   */
  IS_PROD: import.meta.env.PROD,
  
  /**
   * Base URL for the application
   */
  BASE_URL: import.meta.env.BASE_URL,
} as const;

// Feature Flags
export const FEATURES = {
  /**
   * Enable verbose logging
   */
  ENABLE_DEBUG_LOGGING: import.meta.env.VITE_ENABLE_DEBUG_LOGGING === 'true' || ENV.IS_DEV,
  
  /**
   * Enable analytics tracking
   */
  ENABLE_ANALYTICS: import.meta.env.VITE_ENABLE_ANALYTICS === 'true' || ENV.IS_PROD,
  
  /**
   * Enable error reporting (e.g., Sentry)
   */
  ENABLE_ERROR_REPORTING: import.meta.env.VITE_ENABLE_ERROR_REPORTING === 'true' || ENV.IS_PROD,

  /**
   * Enable CMS-driven content for home, solutions, and platform pages.
   *
   * When enabled (true):
   * - Pages fetch content from the SonicJS CMS on mount
   * - If the CMS is unreachable, falls back to hardcoded defaults
   *
   * When disabled (false, the default):
   * - Pages render using hardcoded content only — no CMS calls
   *
   * Set VITE_CMS_CONTENT_ENABLED=true in .env to enable.
   */
  CMS_CONTENT_ENABLED: import.meta.env.VITE_CMS_CONTENT_ENABLED === 'true',
} as const;



// API Configuration
export const API = {
  /**
   * Supabase project URL
   * Set VITE_SUPABASE_URL in .env for production
   */
  SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL || '',
  
  /**
   * Supabase anonymous key
   * Set VITE_SUPABASE_ANON_KEY in .env for production
   */
  SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY || '',
  
  /**
   * Stripe publishable key (frontend only)
   * Set VITE_STRIPE_PUBLISHABLE_KEY in .env for production
   */
  STRIPE_PUBLISHABLE_KEY: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '',
  
  /**
   * HubSpot portal ID
   * Set VITE_HUBSPOT_PORTAL_ID in .env for production
   */
  HUBSPOT_PORTAL_ID: import.meta.env.VITE_HUBSPOT_PORTAL_ID || '',

  /**
   * HubSpot form GUID for Demo Request form
   * Set VITE_HUBSPOT_DEMO_FORM_GUID in .env
   */
  HUBSPOT_DEMO_FORM_GUID: import.meta.env.VITE_HUBSPOT_DEMO_FORM_GUID || '',

  /**
   * HubSpot form GUID for Contact form
   * Set VITE_HUBSPOT_CONTACT_FORM_GUID in .env
   */
  HUBSPOT_CONTACT_FORM_GUID: import.meta.env.VITE_HUBSPOT_CONTACT_FORM_GUID || '',
  
  /**
   * New Relic browser license key
   * Set VITE_NEW_RELIC_BROWSER_LICENSE_KEY in .env for production
   */
  NEW_RELIC_BROWSER_LICENSE_KEY: import.meta.env.VITE_NEW_RELIC_BROWSER_LICENSE_KEY || '',

  /**
   * SonicJS CMS API base URL
   * Set VITE_CMS_API_URL in .env to override
   */
  CMS_API_URL: import.meta.env.VITE_CMS_API_URL || 'https://cms.adventive.com/api',

  /**
   * Media CDN hostname for R2-hosted assets referenced by the CMS.
   * Set VITE_MEDIA_PUBLIC_CDN in .env to override.
   *
   * Production:  https://media.adventive.com
   * Staging:     https://media.adventivestg.com
   * Development: https://media.adventive.dev
   */
  MEDIA_PUBLIC_CDN: import.meta.env.VITE_MEDIA_PUBLIC_CDN || 'https://media.adventive.com',

  /**
   * Gallery preview mode.
   * "embedded" — clicking View Demo navigates to the built-in GalleryPreview page.
   * "external" — clicking View Demo opens the external demo_url in a new tab.
   */
  PREVIEW_MODE: (import.meta.env.VITE_PREVIEW_MODE || 'embedded') as 'embedded' | 'external',
} as const;


// Application Constants
export const APP = {
  /**
   * Application name
   */
  NAME: 'Adventive',
  
  /**
   * Application version
   * TODO: Pull from package.json or build process
   */
  VERSION: '1.0.0',
  
  /**
   * Support email
   */
  SUPPORT_EMAIL: 'support@adventive.com',
  
  /**
   * Trial duration in days
   */
  TRIAL_DURATION_DAYS: 7,
  
  /**
   * ContextIQ monthly price in dollars
   */
  CONTEXTIQ_MONTHLY_PRICE: 500,
} as const;

// Validation
if (ENV.IS_PROD) {
  // Production environment checks
}

// Debug logging
if (FEATURES.ENABLE_DEBUG_LOGGING) {
  console.log('[ENV] Environment configuration loaded:', {
    mode: ENV.MODE,
    isDev: ENV.IS_DEV,
    isProd: ENV.IS_PROD,
    features: FEATURES,
  });
}

export default {
  ENV,
  FEATURES,
  API,
  APP,
};
